import { useState, type FC } from "react";
import Tooltip from "../Tooltip";
import Body from "../../atoms/typography/Body";
import { theme } from "../../../theme";
import IconButton from "../../atoms/buttons/IconButton";
import Modal from "../Modal";
import { Row } from "react-grid-system";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Alert from "../Alert";
import Lookup from "../../organisms/Lookup";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useAddClientEmailConnection } from "../../../graphql/hooks/documentHandler/useAddClientEmailConnection";
import { useConnectInvoiceUploadUnitToClient } from "../../../graphql/hooks/documentHandler/useConnectInvoiceUploadUnitToClientIdentifier";
import { useTranslation } from "react-i18next";
import Subheading from "../../atoms/typography/Subheading";
import { ConnectIconSmall } from "../../atoms/icons/ConnectIcon";
import styled from "styled-components";

type Props = {
  email?: string;
  uploadUnitId: string;
  clientIdentifier?: string;
};

const ConnectOptionContainer = styled.div`
  background-color: ${theme.colors.bgSubtle};
  border-radius: 4px;
  padding: 16px;
`;

const ConnectToClientButton: FC<Props> = ({
  email,
  uploadUnitId,
  clientIdentifier,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<string | undefined>();
  const [clientName, setClientName] = useState<string | undefined>();

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const onSelectLookupResult = (item: any) => {
    setSelectedClient(item?.clientIdentifier);
    setClientName(item?.companyName);
  };

  const onCompletedConnectEmail = (result?: any) => {
    if (result?.client?.addClientEmailConnection?.success === true) {
      setErrorMessage("");
      setSuccessMessage(t("documentHandler.emailHasBeenConnected"));
    } else {
      setSuccessMessage("");
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorConnectEmail = () => {
    setErrorMessage(t("errors.somethingWentWrong"));
  };

  const {
    addClientEmailConnection,
    loading,
    error: emailConnectError,
  } = useAddClientEmailConnection({
    onCompleted: onCompletedConnectEmail,
    onError: onErrorConnectEmail,
  });

  const onClickConnectEmail = async () => {
    if (!selectedClient || !email) {
      setErrorMessage(t("documentHandler.pleaseAssignAnOrganisationNumber"));
    } else {
      try {
        await addClientEmailConnection({
          variables: {
            input: {
              clientIdentifier: selectedClient,
              emailAddress: email,
            },
          },
        });
      } catch (error) {
        setErrorMessage(t("errors.somethingWentWrong"));
      }
    }
  };

  const onCompletedConnectToClient = (response: any) => {
    if (
      response?.documentHandler?.connectInvoiceUploadUnitToClientIdentifier
        ?.success === true
    ) {
      setErrorMessage("");
      setSuccessMessage(t("documentHandler.uploadConnectedToClient"));
    } else {
      setErrorMessage(t("errors.somethingWentWrong"));
    }
  };
  const onErrorConnectToClient = () => {
    setErrorMessage(t("errors.somethingWentWrong"));
  };

  const {
    connectInvoiceUploadUnitToClient,
    loading: connectToClientLoading,
    error: connectToClientError,
  } = useConnectInvoiceUploadUnitToClient({
    onCompleted: onCompletedConnectToClient,
    onError: onErrorConnectToClient,
  });

  const onClickConnectCompany = async () => {
    if (!selectedClient) {
      setErrorMessage(t("documentHandler.pleaseAssignAnOrganisationNumber"));
    } else {
      try {
        await connectInvoiceUploadUnitToClient({
          variables: {
            input: {
              clientIdentifier: selectedClient,
              uploadUnitId: uploadUnitId,
            },
          },
        });
      } catch (error) {
        setErrorMessage(t("errors.somethingWentWrong"));
      }
    }
  };

  return (
    <div>
      <Tooltip
        content={
          <Body color={theme.colors.white}>
            {t("documentHandler.connectUploadUnitToClient")}
          </Body>
        }
      >
        <IconButton
          icon={<ConnectIconSmall color={theme.colors.primary} />}
          color={theme.colors.primary20}
          onClick={() => setModalOpen(true)}
          size="medium"
        />
      </Tooltip>

      {modalOpen && (
        <Modal
          onClose={() => setModalOpen(false)}
          closeIcon
          width={400}
          title={t("documentHandler.connectUploadUnitToClient")}
        >
          <Lookup
            type="dropdown"
            onClickResult={(item) => onSelectLookupResult(item)}
          />
          <HorizontalSpacer spacing={4} />
          {selectedClient && clientName && (
            <Body fontweight="500" color={theme.colors.primary}>
              {t("documentHandler.clientSelection", {
                clientName,
                selectedClient,
              })}
            </Body>
          )}
          <HorizontalSpacer spacing={1} />

          {errorMessage && (
            <Alert
              type="error"
              title={errorMessage}
              message={
                emailConnectError?.toString() ??
                connectToClientError?.toString()
              }
            />
          )}
          {successMessage && <Alert type="success" title={successMessage} />}

          {email && !clientIdentifier && (
            <ConnectOptionContainer>
              <HorizontalSpacer spacing={1} />
              <Subheading color={theme.colors.primary} textalign="center">
                {t("documentHandler.connectEmail")}: {email}
              </Subheading>
              <HorizontalSpacer spacing={1} />
              <Row justify="center">
                <ActionButton
                  title={t("documentHandler.connectEmail")}
                  color={theme.colors.primary}
                  onClick={onClickConnectEmail}
                  icon={<ConnectIconSmall color={theme.colors.white} />}
                  loading={loading}
                  disabled={successMessage.length > 0 || !selectedClient}
                />
              </Row>
              <HorizontalSpacer spacing={1} />
            </ConnectOptionContainer>
          )}
          <HorizontalSpacer spacing={2} />
          <ConnectOptionContainer>
            <HorizontalSpacer spacing={1} />
            <Subheading color={theme.colors.primary} textalign="center">
              {t("documentHandler.connectUploadUnitToClient")}
            </Subheading>
            <HorizontalSpacer spacing={1} />
            <Row justify="center">
              <ActionButton
                title="Koppla uppladdning"
                color={theme.colors.primary}
                onClick={onClickConnectCompany}
                icon={<ConnectIconSmall color={theme.colors.white} />}
                disabled={
                  successMessage.length > 0 || !selectedClient || !uploadUnitId
                }
                loading={connectToClientLoading}
              />
            </Row>
          </ConnectOptionContainer>

          <HorizontalSpacer spacing={4} />
        </Modal>
      )}
    </div>
  );
};

export default ConnectToClientButton;
